import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api, { filesAPI } from '../utils/api';
import {
  Cpu, Upload, AlertTriangle, CheckCircle, Loader, Download, Trash2,
  HardDrive, Pencil, Save, Shield, X, Wrench, Clock, User, Globe, Wifi, Server, FolderUp, Users, Database, PackagePlus
} from 'lucide-react';

const SCRIPT_META = {
  'acm_v133.sh': { label: 'CDC (Virtual Serial Port)', icon: '🔌' },
  'hidkbd_v133.sh': { label: 'HID Keyboard', icon: '⌨️' },
  'hidpos_v133.sh': { label: 'HID-POS', icon: '🏪' },
};

const TIMEZONES = [
  'UTC0', 'IST-2', 'EET-2', 'CET-1', 'GMT0', 'EST5EDT', 'CST6CDT', 'MST7MDT', 'PST8PDT',
  'JST-9', 'CST-8', 'AEST-10', 'NZST-12',
];

export default function FirmwareBuilderPage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const isHe = i18n.language === 'he';
  const isAdmin = user?.role === 'admin';
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [workId, setWorkId] = useState(null);
  const [fwInfo, setFwInfo] = useState(null);
  const [editData, setEditData] = useState({});  // { partLabel: { scripts: {}, network: {}, hostname, timezone, autorun } }
  const [building, setBuilding] = useState(false);
  const [buildResult, setBuildResult] = useState(null);
  const [buildNotes, setBuildNotes] = useState('');
  const [message, setMessage] = useState(null);
  const [fileName, setFileName] = useState('');
  const [builds, setBuilds] = useState([]);
  const [loadingBuilds, setLoadingBuilds] = useState(true);
  const [outputFilename, setOutputFilename] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [publishVisibility, setPublishVisibility] = useState('all');
  const [publishDesc, setPublishDesc] = useState('');
  const [publishUsers, setPublishUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [showPublishFor, setShowPublishFor] = useState(null); // workId of history item to publish

  const baseFileInputRef = useRef(null);
  const [baseFirmwares, setBaseFirmwares] = useState([]);
  const [loadingBases, setLoadingBases] = useState(true);
  const [loadingBaseId, setLoadingBaseId] = useState(null);
  const [uploadingBase, setUploadingBase] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadBuilds();
      loadBaseFirmwares();
      api.get('/profiles/users/list').then(r => setUsersList(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const loadBaseFirmwares = async () => {
    setLoadingBases(true);
    try {
      const { data } = await api.get('/files?includeBase=true');
      setBaseFirmwares(data.filter(f => f.category === 'firmware_base'));
    } catch {} finally { setLoadingBases(false); }
  };

  const handleUploadBase = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingBase(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'firmware_base');
      formData.append('visibility', 'private');
      formData.append('description', isHe ? 'קושחת בסיס' : 'Base firmware');
      await filesAPI.upload(formData);
      await loadBaseFirmwares();
      setMessage({ type: 'success', text: isHe ? `"${file.name}" הועלה כקושחת בסיס` : `"${file.name}" uploaded as base firmware` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message });
    } finally { setUploadingBase(false); }
  };

  const handleLoadBase = async (fw) => {
    setLoadingBaseId(fw.id);
    setMessage(null); setBuildResult(null);
    try {
      const { data } = await api.post(`/firmware/load-base/${fw.id}`);
      setWorkId(data.workId);
      setFileName(data.originalFilename || fw.originalName);
      setFwInfo(data);
      const ed = {};
      for (const part of data.partitions) {
        ed[part.label] = {
          scripts: {},
          network: part.system?.network || {},
          hostname: part.system?.hostname || '',
          timezone: part.system?.timezone || '',
          autorun: null,
          binary: part.binary ? {
            path: part.binary.path,
            vid: part.binary.vid?.value || '',
            vidOffset: part.binary.vid?.offset,
            pids: (part.binary.pids || []).map(p => ({ ...p })),
          } : null,
        };
        for (const [name, info] of Object.entries(part.scripts || {})) {
          ed[part.label].scripts[name] = { ...info.values };
        }
      }
      setEditData(ed);
      setMessage({ type: 'success', text: isHe ? `נטענה "${fw.originalName}" — ערוך את ההגדרות למטה` : `Loaded "${fw.originalName}" — edit settings below` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message });
    } finally { setLoadingBaseId(null); }
  };

  const loadBuilds = async () => {
    setLoadingBuilds(true);
    try { const { data } = await api.get('/firmware/builds/list'); setBuilds(data); } catch {} finally { setLoadingBuilds(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    setMessage(null); setBuildResult(null); setWorkId(null);

    try {
      const buffer = await file.arrayBuffer();
      const token = localStorage.getItem('sophistiscan-token');
      const response = await fetch(`${api.defaults.baseURL}/firmware/upload`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/octet-stream' },
        body: buffer,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setWorkId(data.workId);
      setFwInfo(data);

      // Init edit state from partitions
      const ed = {};
      for (const part of data.partitions) {
        ed[part.label] = {
          scripts: {},
          network: part.system?.network || {},
          hostname: part.system?.hostname || '',
          timezone: part.system?.timezone || '',
          autorun: null, // loaded on demand
          binary: part.binary ? {
            path: part.binary.path,
            vid: part.binary.vid?.value || '',
            vidOffset: part.binary.vid?.offset,
            pids: (part.binary.pids || []).map(p => ({ ...p })),
          } : null,
        };
        for (const [name, info] of Object.entries(part.scripts || {})) {
          ed[part.label].scripts[name] = { ...info.values };
        }
      }
      setEditData(ed);
      setMessage({ type: 'success', text: isHe ? `נמצאו ${data.partitionCount} מחיצות — ערוך את ההגדרות למטה` : `Found ${data.partitionCount} partition(s) — edit settings below` });
    } catch (err) { setMessage({ type: 'error', text: err.message || 'Upload failed' }); } finally { setUploading(false); }
  };

  const handleBuild = async () => {
    if (!workId) return;
    setBuilding(true); setMessage(null); setBuildResult(null);
    try {
      const partUpdates = {};
      for (const [label, data] of Object.entries(editData)) {
        partUpdates[label] = {};
        if (Object.keys(data.scripts).length > 0) partUpdates[label].scripts = data.scripts;
        if (data.network && (data.network.mac || data.network.ip)) partUpdates[label].network = data.network;
        if (data.hostname) partUpdates[label].hostname = data.hostname;
        if (data.timezone) partUpdates[label].timezone = data.timezone;
        if (data.autorun) partUpdates[label].autorun = data.autorun;
        if (data.binary) partUpdates[label].binary = data.binary;
      }
      await api.put(`/firmware/${workId}/values`, { partitions: partUpdates });
      const { data } = await api.post(`/firmware/${workId}/build`, { notes: buildNotes, originalFilename: outputFilename || fileName });
      setBuildResult(data);
      setMessage({ type: 'success', text: isHe ? 'הקושחה נבנתה בהצלחה!' : 'Firmware built successfully!' });
      loadBuilds();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.error || err.message }); }
    finally { setBuilding(false); }
  };

  const handlePublish = async (targetWorkId) => {
    const wId = targetWorkId || workId;
    setPublishing(true);
    try {
      const { data } = await api.post(`/firmware/${wId}/publish`, {
        outputFilename: outputFilename || fileName,
        description: publishDesc || buildNotes,
        visibility: publishVisibility,
        assignedUserIds: publishVisibility === 'assigned' ? publishUsers : [],
      });
      setMessage({ type: 'success', text: isHe ? `הקובץ "${data.filename}" פורסם למערכת הקבצים!` : `File "${data.filename}" published to files!` });
      setShowPublish(false);
      setShowPublishFor(null);
      setPublishDesc('');
      setPublishUsers([]);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Publish failed' });
    } finally { setPublishing(false); }
  };

  const handleDownload = (wId) => {
    const token = localStorage.getItem('sophistiscan-token');
    window.open(`${api.defaults.baseURL}/firmware/${wId || workId}/download?token=${token}`, '_blank');
  };

  const handleDeleteBuild = async (wId) => {
    if (!confirm(isHe ? 'למחוק לצמיתות?' : 'Delete permanently?')) return;
    try { await api.delete(`/firmware/${wId}/full`); setBuilds(prev => prev.filter(b => b.workId !== wId)); } catch {}
  };

  const handleStartOver = async () => {
    if (workId) { try { await api.delete(`/firmware/${workId}`); } catch {} }
    setWorkId(null); setFwInfo(null); setEditData({}); setBuildResult(null); setBuildNotes(''); setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const updateScript = (partLabel, scriptName, field, value) => {
    setEditData(prev => ({
      ...prev, [partLabel]: { ...prev[partLabel], scripts: { ...prev[partLabel].scripts, [scriptName]: { ...prev[partLabel].scripts[scriptName], [field]: value } } }
    }));
  };

  const updateSystem = (partLabel, field, value) => {
    setEditData(prev => ({ ...prev, [partLabel]: { ...prev[partLabel], [field]: value } }));
  };

  const updateNetwork = (partLabel, field, value) => {
    setEditData(prev => ({
      ...prev, [partLabel]: { ...prev[partLabel], network: { ...prev[partLabel].network, [field]: value } }
    }));
  };

  if (!isAdmin) return (
    <div className="p-8 text-center">
      <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
      <h2 className="text-xl font-bold" style={{color:'var(--text-primary)'}}>{isHe ? 'אין הרשאה' : 'Access Denied'}</h2>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{color:'var(--text-primary)'}}>
            <Wrench className="w-7 h-7 text-purple-400" /> {isHe ? 'בניית קושחה' : 'Firmware Builder'}
          </h1>
          <p className="text-sm mt-1" style={{color:'var(--text-muted)'}}>{isHe ? 'העלה firmware, ערוך הגדרות ובנה גרסה חדשה' : 'Upload, edit and build firmware'}</p>
        </div>
        {workId && <button onClick={handleStartOver} className="btn-ghost text-red-400 flex items-center gap-2"><Trash2 className="w-4 h-4" /> {isHe ? 'התחל מחדש' : 'Start Over'}</button>}
      </div>

      {/* Warning */}
      <div className="flex items-start gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-6">
        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm" style={{color:'var(--text-muted)'}}>{isHe ? 'שינוי קושחה יכול לגרום לנזק לסורק. שמור תמיד את הקובץ המקורי.' : 'Modifying firmware can damage the scanner. Always keep the original.'}</p>
      </div>

      {message && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg mb-6 text-sm ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Base Firmwares */}
      {!workId && (
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2" style={{color:'var(--text-primary)'}}>
              <Database className="w-5 h-5 text-cyan-400" />
              {isHe ? 'קושחות בסיס' : 'Base Firmwares'}
            </h3>
            <button
              onClick={() => baseFileInputRef.current?.click()}
              disabled={uploadingBase}
              className="btn-primary flex items-center gap-1.5 text-sm py-1.5 px-3"
              style={{background:'#0891b2'}}
            >
              {uploadingBase ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <PackagePlus className="w-3.5 h-3.5" />}
              {isHe ? 'הוסף קושחת בסיס' : 'Add Base Firmware'}
            </button>
            <input ref={baseFileInputRef} type="file" accept=".img,.bin,.fw" onChange={handleUploadBase} className="hidden" />
          </div>

          {loadingBases ? (
            <div className="flex justify-center py-4"><Loader className="w-5 h-5 animate-spin" style={{color:'var(--text-muted)'}} /></div>
          ) : baseFirmwares.length === 0 ? (
            <div
              onClick={() => baseFileInputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-cyan-500/50 transition-colors"
              style={{borderColor:'var(--border)'}}
            >
              <Database className="w-8 h-8 mx-auto mb-2" style={{color:'var(--text-muted)'}} />
              <p className="text-sm" style={{color:'var(--text-muted)'}}>
                {isHe ? 'אין קושחות בסיס — לחץ להעלאת קושחה ראשונה' : 'No base firmwares — click to upload the first one'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {baseFirmwares.map(fw => (
                <div key={fw.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg px-4 py-3" style={{background:'var(--bg-input)'}}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium font-mono break-all" style={{color:'var(--text-primary)'}}>{fw.originalName}</p>
                    <p className="text-xs mt-0.5" style={{color:'var(--text-muted)'}}>
                      {(fw.fileSize / 1024 / 1024).toFixed(2)} MB
                      {fw.description && fw.description !== 'Base firmware' && fw.description !== 'קושחת בסיס' && ` · ${fw.description}`}
                      {` · ${new Date(fw.createdAt).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleLoadBase(fw)}
                      disabled={loadingBaseId === fw.id}
                      className="btn-primary flex items-center gap-1.5 text-sm py-1.5 px-3"
                      style={{background:'#0891b2'}}
                    >
                      {loadingBaseId === fw.id ? (
                        <Loader className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Wrench className="w-3.5 h-3.5" />
                      )}
                      {isHe ? 'טען לעריכה' : 'Load for editing'}
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm(isHe ? `למחוק את "${fw.originalName}"?` : `Delete "${fw.originalName}"?`)) return;
                        try {
                          await filesAPI.delete(fw.id);
                          setBaseFirmwares(prev => prev.filter(f => f.id !== fw.id));
                        } catch (err) {
                          setMessage({ type: 'error', text: err.response?.data?.error || err.message });
                        }
                      }}
                      disabled={loadingBaseId === fw.id}
                      className="btn-ghost text-red-400 p-1.5"
                      title={isHe ? 'מחק' : 'Delete'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Build History */}
      {!workId && builds.length > 0 && (
        <div className="card p-6 mb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}><Clock className="w-5 h-5 text-blue-400" /> {isHe ? 'בנייות קודמות' : 'Previous Builds'}</h3>
          <div className="space-y-3">
            {builds.map(b => (
              <React.Fragment key={b.id}>
              <div className="flex items-center gap-4 p-4 rounded-lg" style={{background:'var(--bg-input)'}}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-mono font-medium" style={{color:'var(--text-primary)'}}>{b.originalFilename || b.workId}</span>
                    <span className="text-xs px-2 py-0.5 rounded" style={{background:'var(--bg-card)', color:'var(--text-muted)'}}>{new Date(b.createdAt).toLocaleString()}</span>
                  </div>
                  {b.notes && <p className="text-sm mb-1" style={{color:'var(--text-secondary)'}}>{b.notes}</p>}
                  <div className="flex items-center gap-3 text-xs flex-wrap" style={{color:'var(--text-muted)'}}>
                    {b.buildInfo?.modifiedSize && <span>{(b.buildInfo.modifiedSize / 1024 / 1024).toFixed(2)} MB</span>}
                    {b.buildInfo?.partitions && <span>{b.buildInfo.partitions.length} {isHe ? 'מחיצות' : 'partitions'}</span>}
                    {b.builderName && <span className="flex items-center gap-1"><User className="w-3 h-3" />{b.builderName}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {b.hasFiles ? (
                    <>
                      <button onClick={() => handleDownload(b.workId)} className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5" style={{background:'#16a34a'}}><Download className="w-3.5 h-3.5" /> {isHe ? 'הורד' : 'Download'}</button>
                      <button onClick={() => { setOutputFilename(b.originalFilename || b.workId); setPublishDesc(b.notes || ''); setShowPublishFor(b.workId); }}
                        className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5" style={{background:'#7c3aed'}}><FolderUp className="w-3.5 h-3.5" /> {isHe ? 'פרסם' : 'Publish'}</button>
                    </>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-400">{isHe ? 'נמחק' : 'Deleted'}</span>
                  )}
                  <button onClick={() => handleDeleteBuild(b.workId)} className="btn-ghost text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {/* Inline publish panel for this build */}
              {showPublishFor === b.workId && (
                <div className="p-4 rounded-lg border border-purple-500/20 ml-4 mr-4 mb-2" style={{background:'var(--bg-card)'}}>
                  <h4 className="text-sm font-semibold mb-3 flex items-center justify-between" style={{color:'#a78bfa'}}>
                    <span className="flex items-center gap-2"><FolderUp className="w-4 h-4" /> {isHe ? 'פרסום למערכת קבצים' : 'Publish to Files'}</span>
                    <button onClick={() => setShowPublishFor(null)}><X className="w-4 h-4" /></button>
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="label text-xs">{isHe ? 'שם קובץ' : 'Filename'}</label>
                        <input type="text" className="input-field font-mono text-sm" value={outputFilename}
                          onChange={e => setOutputFilename(e.target.value)} />
                      </div>
                      <div>
                        <label className="label text-xs">{isHe ? 'תיאור' : 'Description'}</label>
                        <input type="text" className="input-field text-sm" value={publishDesc}
                          onChange={e => setPublishDesc(e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className="label text-xs">{isHe ? 'נראות' : 'Visibility'}</label>
                      <div className="flex gap-2">
                        {[{ v: 'all', l: isHe ? 'לכולם' : 'All' }, { v: 'assigned', l: isHe ? 'נבחרים' : 'Assigned' }, { v: 'private', l: isHe ? 'פרטי' : 'Private' }].map(opt => (
                          <button key={opt.v} onClick={() => setPublishVisibility(opt.v)}
                            className={`px-3 py-1 rounded-lg text-xs border transition-all ${publishVisibility === opt.v ? 'border-purple-500 bg-purple-500/10 text-purple-300' : 'border-[var(--border)] text-gray-400'}`}>
                            {opt.l}
                          </button>
                        ))}
                      </div>
                    </div>
                    {publishVisibility === 'assigned' && usersList.length > 0 && (
                      <div className="grid grid-cols-2 gap-1 max-h-24 overflow-y-auto">
                        {usersList.map(u => (
                          <label key={u.id} className="flex items-center gap-2 p-1 rounded hover:bg-white/5 cursor-pointer">
                            <input type="checkbox" className="accent-purple-500" checked={publishUsers.includes(u.id)}
                              onChange={e => { if (e.target.checked) setPublishUsers(p => [...p, u.id]); else setPublishUsers(p => p.filter(id => id !== u.id)); }} />
                            <span className="text-xs" style={{color:'var(--text-primary)'}}>{u.displayName}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <button onClick={() => handlePublish(b.workId)} disabled={publishing}
                      className="btn-primary flex items-center gap-2 text-xs" style={{background:'#7c3aed'}}>
                      {publishing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <FolderUp className="w-3.5 h-3.5" />}
                      {publishing ? (isHe ? 'מפרסם...' : 'Publishing...') : (isHe ? 'פרסם' : 'Publish')}
                    </button>
                  </div>
                </div>
              )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Upload */}
      {!workId && (
        <div className="card p-6 mb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}><Upload className="w-5 h-5 text-brand-500" /> {isHe ? 'בנייה חדשה' : 'New Build'}</h3>
          <input ref={fileInputRef} type="file" accept=".img,.bin,.fw" onChange={handleUpload} className="hidden" />
          <div onClick={() => !uploading && fileInputRef.current?.click()} className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-brand-500/50 transition-colors" style={{borderColor:'var(--border)'}}>
            {uploading ? (
              <><Loader className="w-8 h-8 animate-spin text-brand-500 mx-auto" /><p className="mt-3" style={{color:'var(--text-muted)'}}>{isHe ? 'מנתח...' : 'Analyzing...'}</p></>
            ) : (
              <><HardDrive className="w-10 h-10 mx-auto mb-3" style={{color:'var(--text-muted)'}} /><p className="text-sm font-medium" style={{color:'var(--text-primary)'}}>{isHe ? 'לחץ לבחירת קובץ .img' : 'Click to select .img file'}</p></>
            )}
          </div>
        </div>
      )}

      {/* Editor */}
      {workId && fwInfo && (
        <>
          {/* File Info */}
          <div className="card p-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-2"><HardDrive className="w-4 h-4 text-brand-500" /><span className="text-sm font-mono" style={{color:'var(--text-primary)'}}>{fileName}</span></span>
              <span className="text-xs px-2 py-1 rounded" style={{background:'var(--bg-input)', color:'var(--text-muted)'}}>{(fwInfo.fileSize / 1024 / 1024).toFixed(2)} MB</span>
              <span className="text-xs px-2 py-1 rounded" style={{background:'var(--bg-input)', color:'var(--text-muted)'}}>{fwInfo.partitionCount} {isHe ? 'מחיצות' : 'partitions'}</span>
            </div>
          </div>

          {/* Per-partition editors */}
          {fwInfo.partitions.map((part) => {
            const ed = editData[part.label];
            if (!ed) return null;
            const hasScripts = Object.keys(ed.scripts).length > 0;
            const hasBinary = !!ed.binary;
            const hasNetwork = part.system?.network && (part.system.network.mac || part.system.network.ip);
            const hasHostname = part.system?.hostname !== undefined;
            const hasTZ = part.system?.timezone !== undefined;

            return (
              <div key={part.label} className="mb-6">
                {/* Partition header */}
                <div className="flex items-center gap-2 mb-3">
                  <Server className="w-5 h-5 text-purple-400" />
                  <h3 className="font-semibold" style={{color:'var(--text-primary)'}}>
                    {part.type === 'app' ? (isHe ? 'מחיצת אפליקציה' : 'App Partition') : (isHe ? 'מחיצת מערכת' : 'System Partition')}
                  </h3>
                  <span className="text-xs font-mono px-2 py-0.5 rounded" style={{background:'var(--bg-input)', color:'var(--text-muted)'}}>
                    {part.label} @ 0x{part.offset.toString(16).toUpperCase()} ({(part.size / 1024).toFixed(0)} KB)
                  </span>
                </div>

                {/* USB Scripts */}
                {hasScripts && (
                  <div className="space-y-4 mb-4">
                    {Object.entries(ed.scripts).map(([name, values]) => {
                      const meta = SCRIPT_META[name] || { label: name, icon: '📄' };
                      return (
                        <div key={name} className="card p-5">
                          <h4 className="font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
                            <span>{meta.icon}</span> <span>{meta.label}</span>
                            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{background:'var(--bg-input)', color:'var(--text-muted)'}}>{name}</span>
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="label text-xs">VID</label>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-mono" style={{color:'var(--text-muted)'}}>0x</span>
                                <input type="text" className="input-field font-mono" value={values.vid || ''} maxLength={4}
                                  onChange={e => updateScript(part.label, name, 'vid', e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 4))} />
                              </div>
                            </div>
                            <div>
                              <label className="label text-xs">PID</label>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-mono" style={{color:'var(--text-muted)'}}>0x</span>
                                <input type="text" className="input-field font-mono" value={values.pid || ''} maxLength={4}
                                  onChange={e => updateScript(part.label, name, 'pid', e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 4))} />
                              </div>
                            </div>
                            <div>
                              <label className="label text-xs">Manufacturer</label>
                              <input type="text" className="input-field" value={values.manufacturer || ''}
                                onChange={e => updateScript(part.label, name, 'manufacturer', e.target.value)} />
                            </div>
                            <div>
                              <label className="label text-xs">Serial Number</label>
                              <input type="text" className="input-field font-mono" value={values.serial || ''}
                                onChange={e => updateScript(part.label, name, 'serial', e.target.value)} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Binary VID/PID (old firmware without shell scripts) */}
                {hasBinary && (
                  <div className="card p-5 mb-4">
                    <h4 className="font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
                      🔧 {isHe ? 'USB בבינארי (Binary Patch)' : 'USB in Binary (Binary Patch)'}
                      <span className="text-xs font-mono px-2 py-0.5 rounded" style={{background:'var(--bg-input)', color:'var(--text-muted)'}}>{ed.binary.path}</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label text-xs">VID (Vendor ID)</label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono" style={{color:'var(--text-muted)'}}>0x</span>
                          <input type="text" className="input-field font-mono" value={ed.binary.vid || ''} maxLength={4}
                            onChange={e => {
                              const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 4);
                              setEditData(prev => ({...prev, [part.label]: {...prev[part.label], binary: {...prev[part.label].binary, vid: v}}}));
                            }} />
                        </div>
                      </div>
                      {ed.binary.pids?.map((pid, i) => (
                        <div key={i}>
                          <label className="label text-xs">PID — {pid.mode || 'Mode ' + i}</label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono" style={{color:'var(--text-muted)'}}>0x</span>
                            <input type="text" className="input-field font-mono" value={pid.value || ''} maxLength={4}
                              onChange={e => {
                                const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 4);
                                setEditData(prev => {
                                  const newBin = {...prev[part.label].binary};
                                  newBin.pids = [...newBin.pids];
                                  newBin.pids[i] = {...newBin.pids[i], value: v};
                                  return {...prev, [part.label]: {...prev[part.label], binary: newBin}};
                                });
                              }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs mt-3" style={{color:'var(--text-muted)'}}>
                      {isHe ? '⚠️ שינוי ישיר בבינארי — הערכים חייבים להיות 4 תווים hex בדיוק' : '⚠️ Direct binary patch — values must be exactly 4 hex chars'}
                    </p>
                  </div>
                )}

                {/* Network / System Settings */}
                {(hasNetwork || hasHostname || hasTZ) && (
                  <div className="card p-5 mb-4">
                    <h4 className="font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}>
                      <Globe className="w-4 h-4 text-green-400" /> {isHe ? 'הגדרות מערכת ורשת' : 'System & Network Settings'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {hasNetwork && (
                        <>
                          <div>
                            <label className="label text-xs">MAC Address</label>
                            <input type="text" className="input-field font-mono" value={ed.network?.mac || ''}
                              onChange={e => updateNetwork(part.label, 'mac', e.target.value)} placeholder="3a:9d:33:28:d2:22" />
                          </div>
                          <div>
                            <label className="label text-xs">IP Address</label>
                            <input type="text" className="input-field font-mono" value={ed.network?.ip || ''}
                              onChange={e => updateNetwork(part.label, 'ip', e.target.value)} placeholder="192.168.3.122" />
                          </div>
                        </>
                      )}
                      {hasHostname && (
                        <div>
                          <label className="label text-xs">Hostname</label>
                          <input type="text" className="input-field font-mono" value={ed.hostname || ''}
                            onChange={e => updateSystem(part.label, 'hostname', e.target.value)} placeholder="sun8i" />
                        </div>
                      )}
                      {hasTZ && (
                        <div>
                          <label className="label text-xs">{isHe ? 'אזור זמן' : 'Timezone'}</label>
                          <select className="input-field font-mono" value={ed.timezone || 'UTC0'}
                            onChange={e => updateSystem(part.label, 'timezone', e.target.value)}>
                            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!hasScripts && !hasBinary && !hasNetwork && !hasHostname && !hasTZ && (
                  <div className="card p-6 text-center mb-4">
                    <p className="text-sm" style={{color:'var(--text-muted)'}}>{isHe ? 'אין הגדרות ניתנות לעריכה במחיצה זו' : 'No editable settings in this partition'}</p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Build */}
          <div className="card p-6 mb-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-primary)'}}><Save className="w-5 h-5 text-green-400" /> {isHe ? 'בנייה' : 'Build'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="label text-xs">{isHe ? 'שם קובץ הפלט' : 'Output Filename'}</label>
                <div className="flex items-center gap-2">
                  <input type="text" className="input-field font-mono flex-1" value={outputFilename}
                    onChange={e => setOutputFilename(e.target.value)}
                    placeholder={fileName?.replace('.img', '-MOD') || 'firmware-custom'} />
                  <span className="text-sm font-mono" style={{color:'var(--text-muted)'}}>.img</span>
                </div>
              </div>
              <div>
                <label className="label text-xs">{isHe ? 'הערות' : 'Notes'}</label>
                <input type="text" className="input-field text-sm" value={buildNotes} onChange={e => setBuildNotes(e.target.value)}
                  placeholder={isHe ? 'תיעוד השינויים...' : 'Document changes...'} />
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button onClick={handleBuild} disabled={building} className="btn-primary flex items-center gap-2">
                {building ? <Loader className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
                {building ? (isHe ? 'בונה...' : 'Building...') : (isHe ? 'בנה קושחה' : 'Build Firmware')}
              </button>
              {buildResult && (
                <>
                  <button onClick={() => handleDownload()} className="btn-primary flex items-center gap-2" style={{background:'#16a34a'}}>
                    <Download className="w-4 h-4" /> {isHe ? 'הורד' : 'Download'} ({(buildResult.modifiedSize / 1024 / 1024).toFixed(2)} MB)
                  </button>
                  <button onClick={() => setShowPublish(!showPublish)} className="btn-primary flex items-center gap-2" style={{background:'#7c3aed'}}>
                    <FolderUp className="w-4 h-4" /> {isHe ? 'פרסם למערכת קבצים' : 'Publish to Files'}
                  </button>
                </>
              )}
            </div>

            {/* Publish panel */}
            {showPublish && buildResult && (
              <div className="mt-4 p-4 rounded-lg border border-purple-500/20" style={{background:'var(--bg-input)'}}>
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{color:'#a78bfa'}}>
                  <FolderUp className="w-4 h-4" /> {isHe ? 'פרסום למערכת קבצים' : 'Publish to Files System'}
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="label text-xs">{isHe ? 'תיאור הקובץ' : 'File Description'}</label>
                    <input type="text" className="input-field text-sm" value={publishDesc}
                      onChange={e => setPublishDesc(e.target.value)}
                      placeholder={isHe ? 'למשל: גרסה מותאמת ל-Fukuda ECG' : 'e.g. Custom build for Fukuda ECG'} />
                  </div>
                  <div>
                    <label className="label text-xs">{isHe ? 'נראות' : 'Visibility'}</label>
                    <div className="flex gap-2">
                      {[{ v: 'all', l: isHe ? 'לכולם' : 'All users' }, { v: 'assigned', l: isHe ? 'למשתמשים נבחרים' : 'Assigned users' }, { v: 'private', l: isHe ? 'פרטי' : 'Private' }].map(opt => (
                        <button key={opt.v} onClick={() => setPublishVisibility(opt.v)}
                          className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${publishVisibility === opt.v ? 'border-purple-500 bg-purple-500/10 text-purple-300' : 'border-[var(--border)] text-gray-400'}`}>
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  {publishVisibility === 'assigned' && usersList.length > 0 && (
                    <div>
                      <label className="label text-xs">{isHe ? 'שייך למשתמשים' : 'Assign to users'}</label>
                      <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
                        {usersList.map(u => (
                          <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-white/5 cursor-pointer">
                            <input type="checkbox" className="accent-purple-500"
                              checked={publishUsers.includes(u.id)}
                              onChange={e => {
                                if (e.target.checked) setPublishUsers(prev => [...prev, u.id]);
                                else setPublishUsers(prev => prev.filter(id => id !== u.id));
                              }} />
                            <span className="text-xs" style={{color:'var(--text-primary)'}}>{u.displayName}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <button onClick={() => handlePublish()} disabled={publishing}
                    className="btn-primary flex items-center gap-2 text-sm" style={{background:'#7c3aed'}}>
                    {publishing ? <Loader className="w-4 h-4 animate-spin" /> : <FolderUp className="w-4 h-4" />}
                    {publishing ? (isHe ? 'מפרסם...' : 'Publishing...') : (isHe ? 'פרסם' : 'Publish')}
                  </button>
                </div>
              </div>
            )}

            {buildResult && (
              <div className="mt-4 p-4 rounded-lg border border-green-500/20" style={{background:'var(--bg-input)'}}>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-green-500"><CheckCircle className="w-4 h-4" /> {isHe ? 'פרטי בנייה' : 'Build Details'}</h4>
                <div className="text-xs space-y-1" style={{color:'var(--text-muted)'}}>
                  <p>{isHe ? 'תאריך' : 'Date'}: <span className="font-mono" style={{color:'var(--text-primary)'}}>{new Date(buildResult.buildDate).toLocaleString()}</span></p>
                  <p>{isHe ? 'גודל' : 'Size'}: <span className="font-mono" style={{color:'var(--text-primary)'}}>{(buildResult.modifiedSize / 1024 / 1024).toFixed(2)} MB</span></p>
                  {buildResult.partitions?.map((p, i) => (
                    <p key={i}>{p.label}: <span className="font-mono" style={{color:'var(--text-primary)'}}>{(p.newSize / 1024).toFixed(0)} KB</span> (was {(p.originalSize / 1024).toFixed(0)} KB) @ 0x{p.offset.toString(16).toUpperCase()}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
